/**
 * PILGRIM: store locator — shared module (spec:
 * pilgrim-docs/features/store-locator.md, plan: store-locator-phase-3-plan.md).
 *
 * The "list of stores sorted by distance to me" logic lives HERE, not inside
 * the locator section: phase 4's PDP find-in-store drawer imports the same
 * functions and reads the same data island (see pilgrim-store-data.liquid).
 * Everything is progressive enhancement — the store list is server-rendered;
 * this module only reorders, filters and annotates it.
 *
 * Store codes are STRINGS end to end — never parseInt a store code.
 */

/**
 * Reads the store payload from the nearest data island.
 * @param {ParentNode} root
 * @returns {Array<object>} stores ([] when the island is missing/invalid)
 */
export function readStoreData(root = document) {
  const island = root.querySelector('[data-pilgrim-store-data]');
  if (!island?.textContent) return [];
  try {
    const parsed = JSON.parse(island.textContent);
    return Array.isArray(parsed.stores) ? parsed.stores : [];
  } catch {
    console.warn('[pilgrim-store-locator] invalid store data island');
    return [];
  }
}

/**
 * Great-circle distance in kilometres between two {lat, lng} points.
 * ~49 points client-side — no library needed.
 * @param {{lat: number|string, lng: number|string}} a
 * @param {{lat: number|string, lng: number|string}} b
 * @returns {number}
 */
export function haversineKm(a, b) {
  const toRad = (value) => (Number(value) * Math.PI) / 180;
  const earthRadiusKm = 6371;
  const dLat = toRad(b.lat) - toRad(a.lat);
  const dLng = toRad(b.lng) - toRad(a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * earthRadiusKm * Math.asin(Math.sqrt(h));
}

/**
 * Returns a new array of stores annotated with distanceKm, nearest first.
 * @param {Array<object>} stores
 * @param {{lat: number, lng: number}} origin
 */
export function sortByDistance(stores, origin) {
  return stores
    .map((store) => ({ ...store, distanceKm: haversineKm(origin, store) }))
    .sort((a, b) => a.distanceKm - b.distanceKm);
}

/**
 * Normalises text for cross-language matching: a Dane typing "kobenhavn" or
 * "koebenhavn" and a Swede typing "malmo" must all hit. Returns the set of
 * comparable forms for one string.
 * @param {string} text
 * @returns {string[]}
 */
export function searchForms(text) {
  const lower = String(text ?? '')
    .toLowerCase()
    .trim();
  const digraphs = lower
    .replaceAll('æ', 'ae')
    .replaceAll('ø', 'oe')
    .replaceAll('å', 'aa')
    .replaceAll('ä', 'ae')
    .replaceAll('ö', 'oe')
    .replaceAll('ü', 'ue');
  const stripped = lower.normalize('NFD').replace(/[̀-ͯ]/g, '');
  const strippedDigraphless = digraphs
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
  return [...new Set([lower, digraphs, stripped, strippedDigraphless])];
}

/**
 * Filters stores by a free-text query over name, city and postal code.
 * Empty query returns all stores.
 * @param {Array<object>} stores
 * @param {string} query
 */
export function matchStores(stores, query) {
  const queryForms = searchForms(query).filter(Boolean);
  if (queryForms.length === 0) return stores;
  return stores.filter((store) => {
    const haystack = [store.name, store.city, store.postal_code]
      .flatMap((field) => searchForms(field))
      .join('\n');
    return queryForms.some((form) => haystack.includes(form));
  });
}

/**
 * Resolves a free-text query to coordinates using the bundled DK/SE geo
 * dataset (assets/pilgrim-geo-dk-se.json — GeoNames-derived, CC BY 4.0).
 * Resolution order: DK 4-digit postal (exact) → SE 5-digit postal (3-digit
 * prefix centroid) → city/place name (normalized forms). Returns
 * {lat, lng} or null. The single seam to swap for an external geocoder
 * later (see phase-3 plan, session 2.5).
 * @param {{dk: object, se: object, cities: object}|null} geo
 * @param {string} query
 * @returns {{lat: number, lng: number}|null}
 */
export function resolvePlace(geo, query) {
  if (!geo) return null;
  const digits = String(query ?? '').replace(/\D/g, '');
  const compact = String(query ?? '').trim();
  if (digits.length === 4 && digits === compact.replace(/\s/g, '')) {
    const hit = geo.dk?.[digits];
    if (hit) return { lat: hit[0], lng: hit[1] };
  }
  if (digits.length === 5 && /^[\d\s]+$/.test(compact)) {
    const hit = geo.se?.[digits.slice(0, 3)];
    if (hit) return { lat: hit[0], lng: hit[1] };
  }
  if (digits.length === 3 && /^[\d\s]+$/.test(compact)) {
    // A 3-digit query is either a full SE prefix or a partial DK zip.
    const seHit = geo.se?.[digits];
    if (seHit) return { lat: seHit[0], lng: seHit[1] };
    const dkPrefix = prefixCluster(geo.dk, digits);
    if (dkPrefix) return dkPrefix;
  }
  if (digits.length === 0) {
    for (const form of searchForms(query)) {
      const hit = geo.cities?.[form];
      if (hit) return { lat: hit[0], lng: hit[1] };
    }
    // Partial city names resolve as soon as the prefix is unambiguous
    // ("skand…" → Skanderborg before the user finishes typing).
    if (compact.length >= 3) {
      for (const form of searchForms(query)) {
        const hit = prefixCluster(geo.cities, form);
        if (hit) return hit;
      }
    }
  }
  return null;
}

/**
 * Resolves a key prefix against a {key: [lat,lng]} table when every match
 * sits in one geographic cluster (~40 km box) — i.e. the prefix is
 * unambiguous. Returns the shortest matching key's coords (the base
 * place, e.g. "aarhus" over "aarhus n") or null when matches spread.
 * @param {Record<string, [number, number]>|undefined} table
 * @param {string} prefix
 * @returns {{lat: number, lng: number}|null}
 */
export function prefixCluster(table, prefix) {
  if (!table || !prefix) return null;
  let best = null;
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;
  for (const key of Object.keys(table)) {
    if (!key.startsWith(prefix)) continue;
    const [lat, lng] = table[key];
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
    minLng = Math.min(minLng, lng);
    maxLng = Math.max(maxLng, lng);
    if (!best || key.length < best.key.length) best = { key, lat, lng };
  }
  if (!best) return null;
  if (maxLat - minLat > 0.35 || maxLng - minLng > 0.6) return null;
  return { lat: best.lat, lng: best.lng };
}

/**
 * Formats a distance for display using the page language (da/sv use a
 * decimal comma automatically).
 * @param {number} km
 * @returns {string}
 */
export function formatDistanceKm(km) {
  const lang = document.documentElement.lang || 'en';
  const digits = km < 10 ? 1 : 0;
  return Number(km).toLocaleString(lang, {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });
}

/**
 * PilgrimStoreSearch — the ONE search controller shared by the locator
 * section and the phase-4 find-in-store drawer (parity rule: the two
 * surfaces must never diverge — change search behaviour HERE, never in one
 * consumer). Owns the combobox input + typeahead listbox, the lazy geo
 * dataset, the DK/SE chips, the geolocation button and the status line,
 * all looked up inside `root` via the [data-pilgrim-store-*] markup
 * contract (see sections/pilgrim-store-locator.liquid:43-83 — the drawer
 * SSRs the same structure). List rendering stays with the consumer, wired
 * through callbacks:
 *   onReset()            — query cleared: restore the default view
 *   onPlace({lat,lng})   — a typed place resolved OR geolocation succeeded
 *                          (chips are already cleared: new intent)
 *   onText(query)        — free text that is not a known place
 *   onCountry(country)   — chip toggled ('' = cleared)
 */
export class PilgrimStoreSearch {
  /** @type {{dk: object, se: object, cities: object}|null} */
  #geo = null;

  /** @type {Promise<void>|null} */
  #geoLoad = null;

  /** @type {Array<{display: string, forms: string[]}>} */
  #names = [];

  /** @type {string[]} currently rendered suggestion names */
  #suggestions = [];

  #activeIndex = -1;

  #suppressSuggest = false;

  #query = '';

  #country = '';

  #debounce = 0;

  /**
   * @param {ParentNode & Element} root element containing the controls markup
   * @param {{geoUrl?: string, labels: {locating: string, locationError: string},
   *   onReset: () => void, onPlace: (origin: {lat: number, lng: number}) => void,
   *   onText: (query: string) => void, onCountry: (country: string) => void}} options
   */
  constructor(root, options) {
    this.root = root;
    this.options = options;
  }

  /** Raw current query (consumers trim where they need to). */
  get query() {
    return this.#query;
  }

  /** Current chip value ('' = none). */
  get country() {
    return this.#country;
  }

  /** Wires the controls and reveals them. False when the markup is absent. */
  attach() {
    const controls = this.root.querySelector('[data-pilgrim-store-controls]');
    if (!controls) return false;
    controls.hidden = false;
    controls.addEventListener('submit', (event) => event.preventDefault());

    const search = this.root.querySelector('[data-pilgrim-store-search]');
    search?.addEventListener('input', () => {
      this.#query = search.value;
      this.#loadGeo();
      clearTimeout(this.#debounce);
      this.#debounce = setTimeout(() => this.#apply(), 200);
    });
    search?.addEventListener('keydown', (event) => this.#onSearchKeydown(event));
    search?.addEventListener('blur', () => this.#closeSuggestions());

    for (const chip of this.root.querySelectorAll('[data-pilgrim-store-chip]')) {
      chip.addEventListener('click', () => {
        const value = chip.dataset.pilgrimStoreChip ?? '';
        this.#country = this.#country === value ? '' : value;
        for (const other of this.root.querySelectorAll('[data-pilgrim-store-chip]')) {
          other.setAttribute(
            'aria-pressed',
            String(other.dataset.pilgrimStoreChip === this.#country),
          );
        }
        this.options.onCountry(this.#country);
      });
    }

    const locate = this.root.querySelector('[data-pilgrim-store-locate]');
    if (locate) {
      if ('geolocation' in navigator) {
        locate.addEventListener('click', () => this.#locate(locate));
      } else {
        locate.hidden = true;
      }
    }
    return true;
  }

  /** Closes the suggestion panel and clears the status line — the drawer
   * calls this when its dialog closes so a reopen never shows stale state. */
  reset() {
    this.#closeSuggestions();
    this.#status('');
  }

  #loadGeo() {
    if (this.#geo || this.#geoLoad) return;
    const url = this.options.geoUrl;
    if (!url) return;
    this.#geoLoad = fetch(url)
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        this.#geo = data;
        this.#names = (data?.names ?? []).map((display) => ({
          display,
          forms: searchForms(display),
        }));
        // Re-run the pending query now that places can resolve.
        if (this.#query.trim() !== '') this.#apply();
      })
      .catch(() => {
        console.warn('[pilgrim-store-locator] geo dataset failed to load');
      });
  }

  /**
   * Renders up to 8 place suggestions in the custom listbox (the native
   * datalist popup is unstylable browser chrome — this one follows the
   * store design). Hidden when empty or when the query already IS the
   * only suggestion.
   */
  #suggest() {
    const panel = this.root.querySelector('[data-pilgrim-store-suggestions]');
    const search = this.root.querySelector('[data-pilgrim-store-search]');
    if (!panel || !search || this.#names.length === 0) return;
    if (this.#suppressSuggest) {
      this.#suppressSuggest = false;
      this.#closeSuggestions();
      return;
    }
    const query = this.#query.trim();
    const queryForms = searchForms(query).filter(Boolean);
    const matches = [];
    if (query.length >= 2 && !/\d/.test(query) && queryForms.length > 0) {
      for (const name of this.#names) {
        const hit = queryForms.some((queryForm) =>
          name.forms.some((form) => form.startsWith(queryForm)),
        );
        if (!hit) continue;
        matches.push(name.display);
        if (matches.length === 8) break;
      }
    }
    this.#suggestions = matches;
    this.#activeIndex = -1;
    search.removeAttribute('aria-activedescendant');
    if (
      matches.length === 0 ||
      (matches.length === 1 && matches[0].toLowerCase() === query.toLowerCase())
    ) {
      this.#closeSuggestions();
      return;
    }
    panel.replaceChildren(
      ...matches.map((display, index) => {
        const item = document.createElement('li');
        item.id = `${panel.id}-option-${index}`;
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', 'false');
        item.className = 'pilgrim-store-locator__suggestion';
        item.textContent = display;
        // pointerdown (not click) so the pick lands before the input blurs.
        item.addEventListener('pointerdown', (event) => {
          event.preventDefault();
          this.#pick(display);
        });
        return item;
      }),
    );
    panel.hidden = false;
    search.setAttribute('aria-expanded', 'true');
  }

  #closeSuggestions() {
    const panel = this.root.querySelector('[data-pilgrim-store-suggestions]');
    const search = this.root.querySelector('[data-pilgrim-store-search]');
    if (panel) {
      panel.hidden = true;
      panel.replaceChildren();
    }
    this.#suggestions = [];
    this.#activeIndex = -1;
    search?.setAttribute('aria-expanded', 'false');
    search?.removeAttribute('aria-activedescendant');
  }

  /** @param {string} display */
  #pick(display) {
    const search = this.root.querySelector('[data-pilgrim-store-search]');
    if (search) search.value = display;
    this.#query = display;
    this.#suppressSuggest = true;
    this.#apply();
  }

  /** Combobox keyboard support on the search input. @param {KeyboardEvent} event */
  #onSearchKeydown(event) {
    const open = this.#suggestions.length > 0;
    if (event.key === 'Escape') {
      // With suggestions open, Escape means "close the suggestions" and
      // nothing else: preventDefault stops the browser's native input-clear
      // and <dialog> cancel, stopPropagation keeps the drawer's own Escape
      // handler (DialogComponent) from also closing the drawer.
      if (open) {
        event.preventDefault();
        event.stopPropagation();
      }
      this.#closeSuggestions();
      return;
    }
    if (event.key === 'Enter') {
      if (open && this.#activeIndex >= 0) {
        event.preventDefault();
        this.#pick(this.#suggestions[this.#activeIndex]);
      }
      return;
    }
    if (!open || (event.key !== 'ArrowDown' && event.key !== 'ArrowUp')) return;
    event.preventDefault();
    const step = event.key === 'ArrowDown' ? 1 : -1;
    const count = this.#suggestions.length;
    this.#activeIndex = (this.#activeIndex + step + count) % count;
    const panel = this.root.querySelector('[data-pilgrim-store-suggestions]');
    const search = this.root.querySelector('[data-pilgrim-store-search]');
    if (!panel) return;
    [...panel.children].forEach((item, index) => {
      const active = index === this.#activeIndex;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-selected', String(active));
      if (active) search?.setAttribute('aria-activedescendant', item.id);
    });
  }

  /** A search is a new intent — clear any pressed country chip. */
  #resetChips() {
    if (this.#country === '') return;
    this.#country = '';
    for (const chip of this.root.querySelectorAll('[data-pilgrim-store-chip]')) {
      chip.setAttribute('aria-pressed', 'false');
    }
  }

  #status(text) {
    const status = this.root.querySelector('[data-pilgrim-store-status]');
    if (status) status.textContent = text;
  }

  /** Routes the current query: empty → onReset, place → onPlace, else onText. */
  #apply() {
    this.#suggest();
    const query = this.#query.trim();
    if (query === '') {
      this.options.onReset();
      return;
    }
    const place = resolvePlace(this.#geo, query);
    if (place) {
      // A place search is a new intent: chips reset, then the consumer
      // shows every store sorted by distance (chips can then narrow it).
      this.#resetChips();
      this.options.onPlace(place);
      return;
    }
    this.options.onText(query);
  }

  /** @param {HTMLElement} button */
  #locate(button) {
    this.#status(this.options.labels.locating ?? '');
    button.disabled = true;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        button.disabled = false;
        this.#status('');
        this.#resetChips();
        this.options.onPlace({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      },
      () => {
        button.disabled = false;
        this.#status(this.options.labels.locationError ?? '');
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 },
    );
  }
}

/**
 * Builds one drawer store row: the locator card structure (the
 * <pilgrim-store-nearby> client-render, kept identical so shared CSS and
 * the locator element's card handling both apply) plus the availability
 * state as a fourth child. Two states, never a negative one (locked UX):
 * 'in' (green dot) when the store code is in the product's in-stock set,
 * 'maybe' (yellow dot, "not confirmed") for everything else.
 * @param {object} store
 * @param {boolean} inStock
 * @param {{inStock: string, notConfirmed: string}} stateLabels
 */
function buildFindInStoreCard(store, inStock, stateLabels) {
  const item = document.createElement('li');
  item.className = 'pilgrim-store-card';
  item.setAttribute('data-pilgrim-store-card', '');
  item.dataset.code = store.code;
  item.dataset.country = store.country;
  item.dataset.fisState = inStock ? 'in' : 'maybe';
  const link = document.createElement('a');
  link.className = 'pilgrim-store-card__link';
  link.href = store.url;
  const name = document.createElement('span');
  name.className = 'pilgrim-store-card__name';
  name.textContent = store.name;
  link.append(name);
  const address = document.createElement('span');
  address.className = 'pilgrim-store-card__address';
  address.textContent = `${store.street}, ${store.postal_code} ${store.city}`;
  const distance = document.createElement('span');
  distance.className = 'pilgrim-store-card__distance';
  distance.setAttribute('data-pilgrim-store-distance', '');
  distance.hidden = true;
  const state = document.createElement('span');
  state.className = `pilgrim-store-card__state pilgrim-store-card__state--${item.dataset.fisState}`;
  const dot = document.createElement('span');
  dot.className = 'pilgrim-store-card__state-dot';
  dot.setAttribute('aria-hidden', 'true');
  const stateLabel = document.createElement('span');
  stateLabel.textContent = inStock ? stateLabels.inStock : stateLabels.notConfirmed;
  state.append(dot, stateLabel);
  item.append(link, address, distance, state);
  return item;
}

/**
 * Mounts the find-in-store drawer content: clones the block's SSR template
 * (the full locator markup contract with translated labels), injects a
 * store-data island from the fetched payload, client-renders the store
 * cards with availability states into the market-ordered country groups,
 * wires the "only in stock" switch, and inserts the result — at which
 * point the <pilgrim-store-locator> element upgrades and provides search,
 * chips, geolocation and nearest-sorting exactly as on the locator page.
 *
 * @param {HTMLElement} host the <pilgrim-find-in-store> element
 * @param {{stores: Array<object>, inStock: string[]}} data
 * @returns {HTMLElement|null} the mounted <pilgrim-store-locator>, or null
 */
export function mountFindInStore(host, { stores, inStock }) {
  const existing = host.querySelector('pilgrim-store-locator');
  if (existing) return existing;
  const template = host.querySelector('template[data-fis-template]');
  if (!(template instanceof HTMLTemplateElement)) return null;
  const fragment = template.content.cloneNode(true);
  const locator = fragment.querySelector('pilgrim-store-locator');
  if (!locator) return null;

  const inStockSet = new Set(inStock.map(String));
  const stateLabels = {
    inStock: host.dataset.labelInStock ?? '',
    notConfirmed: host.dataset.labelNotConfirmed ?? '',
  };

  // The island the locator element reads (scoped to itself) — same shape
  // as pilgrim-store-data.liquid emits and ?view=json returns.
  const island = document.createElement('script');
  island.type = 'application/json';
  island.setAttribute('data-pilgrim-store-data', '');
  island.textContent = JSON.stringify({ stores, count: stores.length });
  locator.prepend(island);

  // City-then-name ordering, mirroring the locator section's SSR sort.
  const lang = document.documentElement.lang || 'da';
  const sorted = [...stores].sort(
    (a, b) =>
      String(a.city ?? '').localeCompare(String(b.city ?? ''), lang, { sensitivity: 'base' }) ||
      String(a.name ?? '').localeCompare(String(b.name ?? ''), lang, { sensitivity: 'base' }),
  );
  let greenCount = 0;
  for (const store of sorted) {
    const list = locator.querySelector(
      `[data-fis-country-list="${CSS.escape(String(store.country ?? ''))}"]`,
    );
    if (!list) continue;
    const isInStock = inStockSet.has(String(store.code));
    if (isInStock) greenCount += 1;
    list.append(buildFindInStoreCard(store, isInStock, stateLabels));
  }
  // Drop empty country groups (parity with the section's `size > 0` guard).
  for (const list of locator.querySelectorAll('[data-fis-country-list]')) {
    if (list.children.length === 0) list.closest('[data-pilgrim-store-group]')?.remove();
  }

  const toggle = locator.querySelector('[data-fis-only-in-stock]');
  const empty = locator.querySelector('[data-fis-empty]');
  toggle?.addEventListener('change', () => {
    locator.classList.toggle('pilgrim-fis--only-in-stock', toggle.checked);
    if (empty) empty.hidden = !(toggle.checked && greenCount === 0);
  });

  // Inserting the fragment upgrades <pilgrim-store-locator>, which reads
  // the island, indexes the cards and attaches the shared search.
  template.replaceWith(fragment);
  return host.querySelector('pilgrim-store-locator');
}

/**
 * <pilgrim-store-locator> — wires the locator section's SSR list to the
 * functions above. Progressive enhancement only: the full list is in the
 * server HTML; this element reveals the [hidden] controls and then:
 * - LOCATION SEARCH (Matas model): a typed city or postal code resolves to
 *   coordinates via the lazy-loaded bundled geo dataset and re-sorts the
 *   list nearest-first with distances — the same pipeline as the
 *   "Use my location" button. Unrecognized text falls back to a plain
 *   filter over store names/cities/postals (useful mid-typing).
 * - DK/SE toggle chips filter; pressing the active chip clears it.
 * Sorting MOVES the SSR nodes into the "nearest" group and clearing the
 * search MOVES them back — nothing is ever client-rendered.
 */
class PilgrimStoreLocator extends HTMLElement {
  /** @type {Array<object>} */
  #stores = [];

  /** @type {Map<string, HTMLElement>} code -> card element */
  #cards = new Map();

  /** @type {Array<{list: HTMLElement, codes: string[]}>} original grouping */
  #homes = [];

  /** @type {PilgrimStoreSearch|null} */
  #search = null;

  /** 'grouped' | 'near' */
  #mode = 'grouped';

  connectedCallback() {
    this.#stores = readStoreData(this);
    if (this.#stores.length === 0) {
      if (!window.Shopify?.designMode) {
        console.warn('[pilgrim-store-locator] no stores in data island');
      }
      return;
    }

    for (const card of this.querySelectorAll('[data-pilgrim-store-card]')) {
      this.#cards.set(card.dataset.code ?? '', card);
    }
    for (const group of this.querySelectorAll('[data-pilgrim-store-group]')) {
      if (group.dataset.pilgrimStoreGroup === 'nearest') continue;
      const list = group.querySelector('ul');
      if (!list) continue;
      this.#homes.push({
        list,
        codes: [...list.querySelectorAll('[data-pilgrim-store-card]')].map(
          (card) => card.dataset.code ?? '',
        ),
      });
    }

    this.#search = new PilgrimStoreSearch(this, {
      geoUrl: this.dataset.geoUrl,
      labels: {
        locating: this.dataset.labelLocating ?? '',
        locationError: this.dataset.labelLocationError ?? '',
      },
      onReset: () => {
        this.#restoreGrouping();
        this.#refreshVisibility();
      },
      onPlace: (origin) => {
        this.#sortNearest(origin);
        this.#refreshVisibility(this.#stores);
      },
      onText: (query) => {
        if (this.#mode === 'near') this.#restoreGrouping();
        const matched = matchStores(this.#stores, query);
        // Zero matches YET (mid-typing, or a place we don't know): keep the
        // full list on screen — never a dead end, never a layout shift.
        this.#refreshVisibility(matched.length === 0 ? this.#stores : matched);
      },
      onCountry: () => this.#refreshVisibility(),
    });
    this.#search.attach();
  }

  /** Closes any open suggestion panel and clears the status line — the
   * find-in-store drawer calls this on dialog close. */
  resetSearch() {
    this.#search?.reset();
  }

  get stores() {
    return this.#stores;
  }

  /**
   * Applies visibility from an allowed-store list + the country chip.
   * @param {Array<object>} [allowed]
   */
  #refreshVisibility(allowed) {
    const query = this.#search?.query ?? '';
    const country = this.#search?.country ?? '';
    const base = allowed ?? (query.trim() === '' || this.#mode === 'near'
      ? this.#stores
      : matchStores(this.#stores, query));
    const visibleCodes = new Set(
      base
        .filter((store) => country === '' || store.country === country)
        .map((store) => store.code),
    );
    for (const [code, card] of this.#cards) {
      card.hidden = !visibleCodes.has(code);
    }
    for (const group of this.querySelectorAll('[data-pilgrim-store-group]')) {
      const isNearest = group.dataset.pilgrimStoreGroup === 'nearest';
      if (this.#mode === 'near') {
        group.hidden = !isNearest;
        continue;
      }
      if (isNearest) {
        group.hidden = true;
        continue;
      }
      const anyVisible = [...group.querySelectorAll('[data-pilgrim-store-card]')].some(
        (card) => !card.hidden,
      );
      group.hidden = !anyVisible;
    }
  }

  /** Moves every card back to its SSR group in original order. */
  #restoreGrouping() {
    if (this.#mode !== 'near') return;
    for (const home of this.#homes) {
      for (const code of home.codes) {
        const card = this.#cards.get(code);
        if (card) home.list.append(card);
      }
    }
    for (const distance of this.querySelectorAll('[data-pilgrim-store-distance]')) {
      distance.hidden = true;
      distance.textContent = '';
    }
    this.#mode = 'grouped';
  }

  /** @param {{lat: number, lng: number}} origin */
  #sortNearest(origin) {
    const nearestList = this.querySelector('[data-pilgrim-store-nearest-list]');
    if (!nearestList) return;
    const template = this.dataset.labelDistanceTemplate ?? '[d] km';
    for (const store of sortByDistance(this.#stores, origin)) {
      const card = this.#cards.get(store.code);
      if (!card) continue;
      const distance = card.querySelector('[data-pilgrim-store-distance]');
      if (distance) {
        distance.textContent = template.replace('[d]', formatDistanceKm(store.distanceKm));
        distance.hidden = false;
      }
      nearestList.append(card);
    }
    this.#mode = 'near';
  }
}

/**
 * <pilgrim-store-nearby> — "nearby stores" strip on a store page. Reads
 * the shared data island, sorts by distance from this store, renders the
 * closest N as cards. Pure enhancement: the element ships [hidden] and
 * only reveals itself once populated (no-JS visitors never see an empty
 * heading).
 */
class PilgrimStoreNearby extends HTMLElement {
  connectedCallback() {
    const list = this.querySelector('[data-pilgrim-store-nearby-list]');
    const ownCode = this.dataset.code ?? '';
    const origin = { lat: Number(this.dataset.lat), lng: Number(this.dataset.lng) };
    if (!list || !Number.isFinite(origin.lat) || !Number.isFinite(origin.lng)) return;
    const limit = Number(this.dataset.limit) || 4;
    const template = this.dataset.labelDistanceTemplate ?? '[d] km';
    const nearest = sortByDistance(readStoreData(this), origin)
      .filter((store) => store.code !== ownCode)
      .slice(0, limit);
    if (nearest.length === 0) return;
    for (const store of nearest) {
      const item = document.createElement('li');
      item.className = 'pilgrim-store-card';
      const link = document.createElement('a');
      link.className = 'pilgrim-store-card__link';
      link.href = store.url;
      const name = document.createElement('span');
      name.className = 'pilgrim-store-card__name';
      name.textContent = store.name;
      link.append(name);
      const address = document.createElement('span');
      address.className = 'pilgrim-store-card__address';
      address.textContent = `${store.street}, ${store.postal_code} ${store.city}`;
      const distance = document.createElement('span');
      distance.className = 'pilgrim-store-card__distance';
      distance.textContent = template.replace('[d]', formatDistanceKm(store.distanceKm));
      item.append(link, address, distance);
      list.append(item);
    }
    this.hidden = false;
  }
}

if (!customElements.get('pilgrim-store-locator')) {
  customElements.define('pilgrim-store-locator', PilgrimStoreLocator);
}
if (!customElements.get('pilgrim-store-nearby')) {
  customElements.define('pilgrim-store-nearby', PilgrimStoreNearby);
}
